import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles, User, Building2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import CtaBand from "@/components/home/CtaBand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CAREER_SERVICES, CAREER_PACKAGES, ORG_SERVICES } from "@/data/site";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Services"
        description="Career services for professionals — resume writing, LinkedIn optimisation, interview coaching, and a full career transition programme — plus recruitment, RPO, and talent acquisition for organisations."
      />
      <PageHero
        eyebrow="Services"
        title={["Services built for", "your next move."]}
        subtitle="Whether you're a professional building your next move or an organisation building your next team, North Cadence has a track built for you. Choose a tab to see the services that apply to you."
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-[#142A5E]/15 bg-white px-4 py-2 text-sm font-medium text-[#142A5E]">
          <Sparkles className="h-4 w-4 text-[#2FA86A]" />
          Choose "Professionals" or "Organisations" below to get started.
        </span>
      </PageHero>

      <Tabs defaultValue="individuals" data-testid="services-tabs">
        <div className="sticky top-[clamp(64px,7vw,88px)] z-30 border-b border-[#e7e9ef] bg-white/95 backdrop-blur-xl">
          <div className="nc-container flex justify-center py-3">
            <TabsList className="grid h-14 w-full max-w-md grid-cols-2 items-stretch gap-1 rounded-full border border-[#142A5E]/15 bg-[#FEF7F2] p-1.5">
              <TabsTrigger
                value="individuals"
                data-testid="services-tab-individuals"
                className="inline-flex h-full items-center justify-center gap-2 rounded-full text-sm font-semibold text-[#142A5E]/60 transition-colors data-[state=active]:bg-[#142A5E] data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                <User className="h-4 w-4" /> Professionals
              </TabsTrigger>
              <TabsTrigger
                value="organisations"
                data-testid="services-tab-organisations"
                className="inline-flex h-full items-center justify-center gap-2 rounded-full text-sm font-semibold text-[#142A5E]/60 transition-colors data-[state=active]:bg-[#142A5E] data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                <Building2 className="h-4 w-4" /> Organisations
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

      <TabsContent value="individuals" className="mt-0">
      {/* Career services — primary track */}
      <section data-testid="career-services" className="bg-white py-8">
        <div className="nc-container">
          <SectionHeading
            eyebrow="Career Services — For Professionals"
            title={<>Build your career with <span className="text-[#142A5E]">precision.</span></>}
            subtitle="Seven services, one partner. Every engagement is delivered personally by our team — no junior writers, no templates, no third parties."
          />
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2">
            {CAREER_SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <div
                  data-testid={`service-detail-${s.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-[#e7e9ef] bg-[#FEF7F2] p-8 transition-[border-color,background-color] duration-300 hover:border-[#142A5E]/25 hover:bg-white"
                >
                  <div className="flex items-start justify-between">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#142A5E] shadow-sm transition-colors group-hover:bg-[#142A5E] group-hover:text-white">
                      <s.icon className="h-7 w-7" strokeWidth={1.75} />
                    </div>
                    <span className="mt-1 font-display text-sm font-bold text-[#142A5E]/25">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-[#1B1B1B]">{s.title}</h3>
                  <p className="mt-2 leading-relaxed text-[#1B1B1B]/60">{s.desc}</p>
                  <ul className="mt-6 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[15px] text-[#1B1B1B]/75">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2FA86A]" /> {b}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-sm font-medium text-[#1B1B1B]/45">Delivery: {s.delivery}</p>
                  <Link
                    to="/contact"
                    className="group/link mt-5 inline-flex items-center gap-2 font-semibold text-[#142A5E]"
                  >
                    Get started
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Career packages */}
      <section data-testid="career-packages" className="bg-[#FEF7F2] py-8">
        <div className="nc-container">
          <SectionHeading
            align="center"
            eyebrow="Recommended Starting Points"
            title={<>Not sure where to start? <span className="text-[#2FA86A]">Choose a package.</span></>}
            subtitle="Our most-requested service combinations, bundled as clear starting points."
          />
          <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CAREER_PACKAGES.map((p) => (
              <StaggerItem key={p.title}>
                <div className="flex h-full flex-col rounded-3xl border border-[#e7e9ef] bg-white p-7">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#142A5E]/8 text-[#142A5E]">
                    <p.icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#1B1B1B]">{p.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[#1B1B1B]/60">{p.desc}</p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#2FA86A]">{p.meta}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.1}>
            <div className="mt-14 flex justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-[#142A5E] px-8 py-4 font-semibold text-white transition-transform hover:scale-[1.02]"
              >
                Book a Discovery Call to Find Your Package
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      </TabsContent>

      <TabsContent value="organisations" className="mt-0">
      {/* Organisational services — secondary track */}
      <section data-testid="organisational-services" className="bg-white py-8">
        <div className="nc-container">
          <SectionHeading
            eyebrow="Organisational Services — For Startups, SMEs & Growing Organisations"
            title={<>Hire better. <span className="text-[#142A5E]">Grow faster.</span></>}
            subtitle="North Cadence's organisational practice works with startups, SMEs, and growing organisations across six service areas. Each service is scoped to your specific needs — reach out and our team will recommend the right starting point."
          />
          <StaggerGroup className="mt-14 space-y-6">
            {ORG_SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <div
                  data-testid={`org-service-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[&]/g, "")}`}
                  className="relative grid gap-6 rounded-3xl border border-[#e7e9ef] bg-[#FEF7F2] p-8 lg:grid-cols-[auto_1fr]"
                >
                  <span className="absolute right-6 top-6 font-display text-sm font-bold text-[#142A5E]/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex items-start gap-4 lg:flex-col lg:items-start">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#142A5E] text-white">
                      <s.icon className="h-7 w-7" strokeWidth={1.75} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-[#1B1B1B]">{s.title}</h3>
                    <p className="mt-1 font-medium text-[#142A5E]">{s.desc}</p>
                    <p className="mt-3 leading-relaxed text-[#1B1B1B]/65">{s.long}</p>
                    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-[15px] text-[#1B1B1B]/75">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2FA86A]" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal delay={0.1}>
            <div className="mt-14 flex justify-center">
              <Link
                to="/contact"
                data-testid="org-services-enquire"
                className="group inline-flex items-center gap-2 rounded-full border border-[#142A5E]/20 px-8 py-4 font-semibold text-[#142A5E] transition-colors hover:border-[#142A5E]"
              >
                Enquire about organisational services
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      </TabsContent>
      </Tabs>

      <CtaBand
        heading={<>Not sure which service<br /> <span className="text-[#2FA86A]">fits your situation?</span></>}
        subtitle="Book a free 30-minute discovery call. We'll listen, ask the right questions, and recommend the right starting point — no pressure, no pitch."
        showServicesLink={false}
      />
    </div>
  );
}
